import React, { useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (newPassword: string) => Promise<void>;
  accountName?: string;
  loading?: boolean;
}

const PasswordResetModal: React.FC<Props> = ({ 
  open, 
  onClose, 
  onSubmit, 
  accountName,
  loading = false 
}) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);
      await onSubmit(values.newPassword);
      form.resetFields();
      onClose();
      message.success('비밀번호가 성공적으로 재설정되었습니다.');
    } catch (error) {
      message.error('비밀번호 재설정에 실패했습니다.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={`비밀번호 재설정${accountName ? ` - ${accountName}` : ''}`}
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          취소
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={confirmLoading || loading}
          onClick={handleSubmit}
        >
          재설정
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ newPassword: '', confirmPassword: '' }}
      >
        <Form.Item
          label="새 비밀번호"
          name="newPassword"
          rules={[
            { required: true, message: '새 비밀번호를 입력해주세요.' },
            { min: 4, message: '비밀번호는 최소 4자 이상이어야 합니다.' },
          ]}
        >
          <Input.Password
            placeholder="새 비밀번호를 입력하세요"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label="비밀번호 확인"
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '비밀번호를 다시 입력해주세요.' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('비밀번호가 일치하지 않습니다.'));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="비밀번호를 다시 입력하세요"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PasswordResetModal; 